<template lang="pug">
td.Table-cell(
  @click='activate()',
  @dblclick='edit()',
  :class='{ "Table-cell--oneLine": isOneLine, "Table-cell--active": isActive, "Table-cell--editing": isEditable, "Table-cell--editable": cell.isEditable }',
  :style='{ background: cell.color || "transparent" }'
)
  span(
    v-if='!isEditable',
    :style='{ "min-height": "28px", "font-weight": isBold ? "500" : "normal", "font-style": isItalic ? "italic" : "normal", color: fontColor }'
  ) {{ formatCellValue(cell.value) }}
  span(v-else)
    input(
      type='text',
      @input='onChangeInput',
      v-model='value',
      ref='inputField'
    )
</template>

<script>
import { DateTime } from 'luxon'

export default {
  name: 'excel-cell',
  data() {
    return {
      value: '',
      dblClick: false,
      isBold: false,
      isItalic: false,
      fontColor: '#000000',
      leftBorder: null,
      rightBorder: null,
      topBorder: null,
      bottomBorder: null
    }
  },
  props: {
    cell: {
      type: Object,
      default: () => {
        return {}
      }
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    isOneLine: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    },
    forceInput: {
      type: String,
      default: ''
    }
  },
  watch: {
    isEditable(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.changeValue()
      }
      if (newVal) {
        this.beforeEditValue()
      }
    }
  },
  beforeDestroy() {
    if (this.isEditable) {
      this.changeValue()
    }
  },
  mounted() {
    if (this.cell.style && this.cell.style.border) {
      this.leftBorder = this.cell.style.border.left
      this.rightBorder = this.cell.style.border.right
      this.topBorder = this.cell.style.border.top
      this.bottomBorder = this.cell.style.border.bottom
    }

    if (this.cell.style && this.cell.style.font) {
      this.isBold = this.cell.style.font.bold
      this.isItalic = this.cell.style.font.italic
      this.fontColor = this.cell.style.font.color && this.cell.style.font.color.argb ? '#' + this.cell.style.font.color.argb.substring(2) : ''
    }
    if (this.fontColor === '#000000') {
      this.fontColor = ''
    }
  },
  methods: {
    activate() {
      if (!this.dblClick && !this.isEditable) {
        setTimeout(() => {
          if (!this.dblClick) {
            this.$emit('activate')
          }
        }, 200)
      }
    },
    edit() {
      if (this.cell.isEditable && !this.isEditable) {
        this.dblClick = true
        setTimeout(() => {
          this.dblClick = false
        }, 300)
        this.$emit('editMode')
      }
    },
    beforeEditValue() {
      setTimeout(() => {
        this.$refs.inputField.focus()
        if (this.forceInput) {
          this.value = this.forceInput
        } else {
          this.value = (this.cell.value || this.cell.value === 0) && this.cell.value.result ? this.cell.value.result : this.cell.value || ''
        }
      })
    },
    onChangeInput() {
      /*
       * [0-9]+ - 1 or more numbers 0-9
       * [\.,] - possible delimiters
       * [0-9]* - after delimiter we might have numbers
       * \s?%? space is possible before %
       */
      const regex = /[0-9]+[\.,]?[0-9]*\s?%?/g
      const matched = this.value.match(regex)
      this.value = matched && matched.length > 0 ? matched[0] : ''
    },
    changeValue() {
      this.$emit('changeValue', this.value.toString().trim())
    },

    /**
     * Форматирование значения ячейки
     */
    formatCellValue(value) {
      //Форматирование даты (и времени)
      if (value instanceof Date) {
        if (value.getHours() || value.getMinutes || value.getSeconds || value.getMilliseconds) {
          //Если есть время, то предобразуем в формат со временем
          return DateTime.fromJSDate(value).toFormat('dd.MM.yyyy HH:mm:ss')
        } else {
          //Если веремение нет, то только дата
          return DateTime.fromJSDate(value).toFormat('dd.MM.yyyy')
        }
      }

      //Форматирование числа
      if (typeof value === 'number') {
        //Форматтер дробной части
        //Согласно задаче https://app.clickup.com/t/2n42zp2
        //После запятой оставляем 4 значащие цифры
        const fractionPartFormatter = new Intl.NumberFormat('ru-RU', {
          maximumSignificantDigits: 4
        })

        //Форматтер целой части, чтобы были разделители разрядов
        const formatter = new Intl.NumberFormat('ru-RU', {
          maximumFractionDigits: 0
        })

        const fractionPart = value - Math.floor(value)

        if (fractionPart) {
          return `${formatter.format(Math.floor(value))}.${fractionPartFormatter.format(fractionPart).slice(2)}`
        } else {
          return formatter.format(value)
        }
      }

      return value
    }
  }
}
</script>

<style lang="scss">
@import '~base';
#app .Table-cell,
.Popup .Table-cell {
  --borderColor: #{$white2};
  --editingBorderColor: #{$black};
}

#app[data-theme='dark'] .Table-cell {
  --borderColor: #{$black1};
  --editingBorderColor: #{$blue};
}

.Popup[data-theme='dark'] .Table-cell {
  --borderColor: #{$black1};
  --editingBorderColor: #{$blue};
}
</style>
<style scoped lang="scss">
@import '~base';
/*.Table {
    &-cell {
      position: relative;
      cursor: default;
      vertical-align: middle;
      min-height: 38px;
      min-width: 40px;
      padding: 5px;
      border: 2px solid var(--borderColor);
    }
  }*/

.Table {
  &-cell {
    position: relative;
    cursor: default;
    vertical-align: middle;
    min-height: 38px;
    min-width: 40px;
    padding: 5px;
    border-bottom: 1px solid var(--borderColor);
    border-right: 1px solid var(--borderColor);

    span {
      display: flex;
      align-items: center;
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
      padding: 0 5px;
      color: var(--basicText);
      z-index: 2;
    }

    &--oneLine {
      span {
        white-space: nowrap;
        overflow: visible;
      }
    }

    &--bottomBorder:before,
    &--topBorder:before,
    &--leftBorder:before,
    &--rightBorder:before,
    &--active:before,
    &--editing:before {
      content: '';
      position: absolute;
      top: 0px;
      left: 0px;
      right: 0px;
      bottom: 0px;
      z-index: 2;
    }

    &--editable {
      span {
        color: #0288f3 !important;
      }
    }

    &--bottomBorder:before {
      border-bottom: 1px solid var(--borderColor);
    }

    &--topBorder:before {
      border-top: 1px solid var(--borderColor);
    }

    &--leftBorder:before {
      border-left: 1px solid var(--borderColor);
    }

    &--rightBorder:before {
      border-right: 1px solid var(--borderColor);
    }

    &--active:before {
      border: 2px solid var(--editingBorderColor) !important;
    }

    &--editing:before {
      border: 3px solid var(--editingBorderColor) !important;
    }
  }

  input[type='text'] {
    display: block;
    position: relative;
    padding: 0;
    width: 100%;
    height: 100%;
    border: 0;
    background: transparent;
    box-sizing: border-box;

    &:focus {
      outline: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/objects/infrastructure/ExcelCell.vue